import React, { useState } from "react";
import { useDroppable } from "@dnd-kit/react";

import EditableText from "../../../../../../../components/Inputs/EditableText";
import RouteColorSwatch from "../../../../../../../components/Maps/ActiveRoutes/RouteColorSwatch";
import { resolveRouteColorPalette } from "../../../../../../../components/Maps/ActiveRoutes/routeColor";
import NotesIcon from "../../../../../../../resources/route-builder/notes.svg";
import RefreshIcon from "../../../../../../../resources/route-builder/refresh.svg";
import { useStopSequence } from "../stops/useStopSequence";
import { useRouteBuilder } from "../../../context/RouteBuilderContext";
import {
  incorporatesTime,
  routeSummaryText,
  shouldShowDropPropertiesHint,
} from "../../../lib/helpers";
import AddPropertyVisit from "../stops/AddPropertyVisit";
import { AddRouteButton, RemoveRouteButton } from "./RouteActions";
import RouteEndpointsEditor from "./RouteEndpointsEditor";

export default function SelectedRoutePanel({
  expanded = false,
}: {
  expanded?: boolean;
}) {
  const {
    session,
    selectedRouteId,
    renameRoute,
    setRouteColor,
    setRouteInstructions,
    dirtyRouteIds,
    reoptimizeRoute,
    isBusy,
    optimizationBlocked,
  } = useRouteBuilder();

  const route =
    session?.routes.find((r) => r.id === selectedRouteId) ??
    session?.routes[0] ??
    null;

  const [showInstructions, setShowInstructions] = useState(false);

  const { ref, isDropTarget } = useDroppable({
    id: route ? `route-panel-drop-${route.id}` : "route-panel-drop-none",
    type: "route-column",
    accept: "stop",
    // Below slots (30) and cards (20); stays enabled once the route has stops
    // so a drop in the space under the stops appends instead of needing the
    // 16px gap. Must beat UnassignedPool (5).
    collisionPriority: 10,
    data: { routeId: route?.id },
    disabled: !route,
  });
  // The header above the stops acts as the first slot, so a drop up there
  // goes to the start instead of the end. Beats the panel (10).
  const { ref: headerDropRef } = useDroppable({
    id: route ? `route-panel-start-${route.id}` : "route-panel-start-none",
    type: "stop-slot",
    accept: "stop",
    collisionPriority: 15,
    data: { routeId: route?.id, index: 0 },
    disabled: !route,
  });

  const colorClass = route
    ? resolveRouteColorPalette(route.color).bg
    : "bg-slate-400";
  const stopItems = useStopSequence(
    route?.id ?? "none",
    route?.stops ?? [],
    colorClass,
  );

  if (!route) {
    return (
      <div
        className="flex flex-1 items-center justify-center bg-slate-50 text-sm text-slate-500"
        data-testid="selected-route-panel"
      >
        <div className="text-center">
          <p className="mb-2">No routes in this plan</p>
          <AddRouteButton />
        </div>
      </div>
    );
  }

  const isDirty = dirtyRouteIds.has(route.id);

  return (
    <section
      ref={ref}
      className={`flex min-h-0 min-w-0 flex-col overflow-hidden border-slate-200 bg-white ${
        expanded ? "flex-1" : "w-full flex-1 md:border-r"
      } ${isDropTarget ? "ring-2 ring-inset ring-sky-300" : ""}`}
      data-testid="selected-route-panel"
    >
      <header
        ref={headerDropRef}
        className="shrink-0 border-b border-slate-100 px-4 py-3"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <RouteColorSwatch
              color={route.color}
              testId={`panel-route-color-${route.id}`}
              onChange={(next) => setRouteColor(route.id, next)}
            />
            <EditableText
              value={route.name}
              testId={`panel-route-name-${route.id}`}
              editLabel="Edit route name"
              className="text-base"
              onSave={(next) => renameRoute(route.id, next)}
            />
          </div>
          <RemoveRouteButton route={route} />
        </div>
        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
          <span>
            {routeSummaryText(route, {
              incorporateTime: incorporatesTime(session?.inputs),
              includeDistance: true,
            })}
          </span>
        </p>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-800"
            data-testid="toggle-route-instructions"
            onClick={() => setShowInstructions((v) => !v)}
          >
            <img src={NotesIcon} alt="" className="h-3.5 w-3.5" aria-hidden />
            {showInstructions
              ? "Done"
              : route.instructions
                ? "Edit instructions"
                : "Add instructions"}
          </button>
          {isDirty ? (
            <button
              type="button"
              data-testid={`panel-reoptimize-route-${route.id}`}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
                isDirty
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                  : "border-slate-300 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
              }`}
              disabled={isBusy || optimizationBlocked}
              onClick={() => void reoptimizeRoute(route.id)}
            >
              <img
                src={RefreshIcon}
                alt=""
                className="h-3.5 w-3.5"
                aria-hidden
              />
              Re-optimize route
            </button>
          ) : null}
        </div>
        {showInstructions && (
          <textarea
            data-testid={`route-instructions-${route.id}`}
            className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-700"
            rows={3}
            placeholder="e.g. Pin the hospital first, then do two properties and return…"
            value={route.instructions ?? ""}
            onChange={(e) => setRouteInstructions(route.id, e.target.value)}
          />
        )}
        <RouteEndpointsEditor route={route} />
      </header>

      <ol
        className="flex flex-1 flex-col gap-2 overflow-y-auto p-3"
        data-testid={`route-stops-${route.id}`}
      >
        {stopItems}
        {route.stops.length === 0 &&
        session &&
        shouldShowDropPropertiesHint(session) ? (
          <li
            className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500"
            data-testid="drop-properties-hint"
          >
            Drop properties here
          </li>
        ) : null}
        <li className="list-none">
          <AddPropertyVisit routeId={route.id} />
        </li>
      </ol>
    </section>
  );
}
